import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { Pin, ChevronRight, Briefcase, User as UserIcon, Loader2 } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

/**
 * AllTopPicks — surfaces all pinned top picks across every list the user can access.
 * Rendered above the All-tab task list. Click a list-name chip to switch to that list,
 * click a task-linked pick to open the task.
 */
export default function AllTopPicks({ tasks, lists, onOpenTask, onPickList }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/top-picks/all`);
      setRows(data);
    } catch {
      /* silent */
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  if (loading) return null;
  if (rows.length === 0) return null;

  // Group by list_id, preserve order based on list order in sidebar
  const listOrder = Object.fromEntries(lists.map((l, i) => [l.id, i]));
  const grouped = {};
  for (const r of rows) {
    if (!grouped[r.list_id]) grouped[r.list_id] = { list_id: r.list_id, list_name: r.list_name, list_category: r.list_category, is_owned: r.is_owned, owner_name: r.owner_name, owner_email: r.owner_email, picks: [] };
    grouped[r.list_id].picks.push(r);
  }
  const groups = Object.values(grouped).sort((a, b) => (listOrder[a.list_id] ?? 999) - (listOrder[b.list_id] ?? 999));

  return (
    <section className="mb-6 border border-zinc-200 bg-gradient-to-r from-amber-50/60 to-orange-50/40" data-testid="all-top-picks">
      <div className="flex items-center justify-between px-4 py-2.5 border-b border-zinc-200">
        <div className="flex items-center gap-2">
          <Pin className="w-3.5 h-3.5 text-amber-700 rotate-45" strokeWidth={2} />
          <p className="text-[10px] font-semibold uppercase tracking-[0.18em] text-zinc-700">
            Top picks · across {groups.length} list{groups.length === 1 ? "" : "s"}
          </p>
        </div>
        <p className="text-[10px] text-zinc-400 hidden sm:block">{rows.length} pinned</p>
      </div>

      <div className="divide-y divide-zinc-200">
        {groups.map((g) => {
          const CatIcon = g.list_category === "personal" ? UserIcon : Briefcase;
          return (
            <div key={g.list_id} className="px-4 py-2.5" data-testid={`all-top-picks-group-${g.list_id}`}>
              <button
                type="button"
                onClick={() => onPickList?.(g.list_id)}
                className="flex items-center gap-1.5 text-[10px] uppercase tracking-[0.15em] text-zinc-500 hover:text-[#002FA7] mb-1.5 transition-colors"
                data-testid={`all-top-picks-jump-${g.list_id}`}
              >
                <CatIcon className="w-3 h-3" strokeWidth={1.5} />
                <span className="font-semibold tracking-[0.12em]">{g.list_name}</span>
                {!g.is_owned && (
                  <span className="text-[9px] normal-case tracking-normal text-emerald-700 px-1 py-0.5 border border-emerald-300 bg-emerald-50">
                    from {g.owner_name || g.owner_email}
                  </span>
                )}
                <ChevronRight className="w-3 h-3 ml-0.5 opacity-50" />
              </button>
              <ul className="space-y-0.5">
                {g.picks.map((p) => {
                  const linkedTask = p.kind === "task" ? (tasks || []).find((t) => t.id === p.task_id) : null;
                  const label = linkedTask ? linkedTask.title : (p.label || "(no label)");
                  const completed = linkedTask?.completed;
                  return (
                    <li key={p.id} className="flex items-center gap-2 group" data-testid={`all-top-pick-${p.id}`}>
                      <span className="text-[10px] font-mono text-zinc-400 w-5 shrink-0">{(p.order || 0) + 1}.</span>
                      {linkedTask ? (
                        <button
                          type="button"
                          onClick={() => onOpenTask?.(linkedTask)}
                          className="flex-1 text-left text-sm text-zinc-800 hover:text-[#002FA7] truncate"
                        >
                          <span className={completed ? "line-through text-zinc-400" : ""}>{label}</span>
                        </button>
                      ) : (
                        <span className="flex-1 text-sm text-zinc-700 truncate">{label}</span>
                      )}
                    </li>
                  );
                })}
              </ul>
            </div>
          );
        })}
      </div>
    </section>
  );
}
